// Client-side guard for the post-login `return_to` destination.
//
// When a logged-out user opens a deployed app (/apps/<name>/...), the server
// proxy bounces them to /?return_to=/apps/<name>/... . After Okta login we
// navigate the browser to that value with window.location.replace(). A
// prefix-only "starts with /apps/" check is NOT enough: the browser normalizes
// dot-segments, so a crafted value like `/apps/foo/../../admin` (or percent-
// encoded variants such as `/apps/foo/%2e%2e/%2e%2e/admin`, even double-encoded)
// would collapse to `/admin` and escape the apps mount. This mirrors — and
// slightly hardens — the server's sanitizeReturnTo() in server/routes/deploy.js.
//
// Returns the original (validated) string if it is a safe in-app destination,
// or null otherwise.
export function sanitizeReturnTo(raw) {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 2000) return null;

  // Decode repeatedly so single- or double-encoded dot-segments and slashes
  // can't slip past the segment check below. Bail out on malformed escapes.
  const forms = [raw];
  let cur = raw;
  for (let i = 0; i < 3; i++) {
    let dec;
    try { dec = decodeURIComponent(cur); } catch { return null; }
    if (dec === cur) break;
    forms.push(dec);
    cur = dec;
  }

  for (const v of forms) {
    if (/[\u0000-\u001f\u007f\\]/.test(v)) return null; // control chars, CR/LF, backslash
    if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return null;    // absolute scheme (http:, javascript:, …)
    if (v.startsWith('//')) return null;                // protocol-relative //evil.com
    if (!v.startsWith('/apps/')) return null;           // must land inside the apps mount
    if (v.split('/').some((s) => s === '..' || s === '.')) return null; // any dot-segment
  }

  return raw;
}

export default sanitizeReturnTo;
